import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { sensitiveEnv } from "../../../config/sensitiveEnv";
import "../../../config/styles/theme.less";
import {
  type IMapComponentProps,
  type IMapComponentRef,
} from "../../interfaces/map.interface";
import MapErrorOverlay from "../MapErrorOverlay";
import { DEFAULT_MAP_COORDINATES, MAP_STYLE } from "./constants";
import "./style.less";

mapboxgl.accessToken = sensitiveEnv.mapboxToken;

const isValidCoordinate = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const MapComponent = forwardRef<IMapComponentRef, IMapComponentProps>(
  (props, ref) => {
    const { lat, lon, onLoad } = props;

    const safeLat = isValidCoordinate(lat) ? lat : DEFAULT_MAP_COORDINATES.lat;
    const safeLon = isValidCoordinate(lon) ? lon : DEFAULT_MAP_COORDINATES.lon;

    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<mapboxgl.Map | null>(null);
    const [hasMapError, setHasMapError] = useState(false);

    useEffect(() => {
      if (!mapContainerRef.current || map !== null || hasMapError) {
        return;
      }

      if (!mapboxgl.accessToken) {
        setHasMapError(true);
        return;
      }

      let mapInstance: mapboxgl.Map | null = null;
      try {
        mapInstance = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: MAP_STYLE,
          center: [safeLat, safeLon],
          zoom: 5,
          maxZoom: 50,
          attributionControl: false,
        });

        mapInstance.addControl(
          new mapboxgl.NavigationControl({
            showCompass: false,
            visualizePitch: true,
            showZoom: true,
          }),
          "bottom-right"
        );

        mapInstance.on("error", (event) => {
          console.error("Mapbox error:", event?.error ?? event);
        });

        mapInstance.on("load", () => {
          if (onLoad && mapInstance) {
            try {
              onLoad(mapInstance);
            } catch (loadError) {
              console.error("Map onLoad handler failed:", loadError);
              setHasMapError(true);
            }
          }
        });
        setMap(mapInstance);
      } catch (error) {
        console.error("Failed to initialize Mapbox map:", error);
        if (mapInstance) {
          try {
            mapInstance.remove();
          } catch {
            // ignore cleanup errors
          }
        }
        setHasMapError(true);
      }
    }, [map, onLoad, safeLat, safeLon, hasMapError]);

    // Separate from the creation effect above so that `map` remains the only
    // dependency — the creation effect's other deps (onLoad, safeLat, safeLon)
    // change identity on nearly every parent render, and tying removal to
    // those would tear the map down long before the component actually
    // unmounts.
    useEffect(() => {
      return () => {
        try {
          map?.remove();
        } catch {
          // ignore cleanup errors
        }
      };
    }, [map]);

    // The map's projection is initialized against whatever size the container
    // happens to be at construction time, which in a flexbox/dynamic layout
    // can be before the surrounding page has settled to its final size. If
    // that later changes without map.resize() being called, the WebGL canvas
    // visually rescales for free (plain CSS stretching), but anything
    // positioned via map.project() — e.g. plot number label markers — is
    // computed from the stale projection and ends up visibly off. A
    // ResizeObserver on the container re-syncs the map whenever its real
    // size changes, including the very first time it settles (see
    // mapbox-gl-js issues #8982, #3265 and react-mapbox-gl #395/#372).
    useEffect(() => {
      if (!map || !mapContainerRef.current) return;

      const observer = new ResizeObserver(() => {
        map.resize();
      });
      observer.observe(mapContainerRef.current);

      return () => {
        observer.disconnect();
      };
    }, [map]);

    useImperativeHandle(
      ref,
      () => ({
        getMap: () => map,
        mapContainerRef: mapContainerRef.current,
      }),
      [map, mapContainerRef]
    );

    return (
      <div
        ref={mapContainerRef}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          width: "100%",
          zIndex: 0,
        }}
      >
        {hasMapError && <MapErrorOverlay />}
        {props.children}
      </div>
    );
  }
);

MapComponent.displayName = "MapComponent";
export default React.memo(MapComponent);
